// Утилиты для работы с шаблонами отчетов и их интеграции с медиа-новостями
import { parseTimeline, calculateStageDurations, getTotalDuration } from './timelineParser';

/**
 * Применяет шаблон отчета к данным и возвращает обработанные результаты
 * @param {Array} data - исходные данные
 * @param {Object} templateConfig - конфигурация шаблона
 * @returns {Object} - обработанные данные с группировкой и агрегацией
 */
export const applyReportTemplate = (data, templateConfig) => {
  if (!data || data.length === 0) {
    return { processedData: [], summary: {} };
  }

  console.log('Применяем шаблон к данным:', {
    originalDataCount: data.length,
    templateName: templateConfig.name,
    dateRange: templateConfig.dateRange,
    filtersCount: templateConfig.filters?.length || 0
  });

  // Обогащаем данные временными метриками из таймлайна
  let filteredData = data.map(item => {
    const timelineFieldId = 'ufCrm3_1757404689894';
    const timelineText = item[timelineFieldId];

    if (!timelineText) {
      return item;
    }

    // Парсим таймлайн и вычисляем длительности
    const events = parseTimeline(timelineText);
    if (events.length === 0) {
      return item;
    }

    const stageDurations = calculateStageDurations(events);
    const totalDuration = getTotalDuration(timelineText);

    // Добавляем временные метрики для каждой стадии
    const enrichedItem = { ...item };

    // Общее время
    if (totalDuration) {
      enrichedItem._totalProductionTime = parseFloat(totalDuration.durationHours);
    }

    // Время на каждой стадии
    stageDurations.forEach(duration => {
      const stageName = duration.stage;
      const fieldName = `_time_${stageName.toLowerCase().replace(/\s+/g, '_')}`;
      enrichedItem[fieldName] = duration.durationHours;
    });

    // Также добавляем поля для агрегаций (например, timeline_avg_stage_резка_стекла)
    // Для каждой агрегации типа timeline_*_stage добавляем индивидуальное время
    templateConfig.aggregations?.forEach(agg => {
      if (agg.type && agg.type.includes('_stage') && agg.stageName) {
        const stageDuration = stageDurations.find(d =>
          d.stage.toLowerCase().includes(agg.stageName.toLowerCase())
        );
        if (stageDuration && agg.outputField) {
          enrichedItem[agg.outputField] = stageDuration.durationHours;
        }
      } else if (agg.type && agg.type.includes('_total')) {
        // Для общего времени
        if (totalDuration && agg.outputField) {
          enrichedItem[agg.outputField] = parseFloat(totalDuration.durationHours);
        }
      }
    });

    return enrichedItem;
  });

  // Дополнительная фильтрация по дате (если данные не были загружены с сервера по периоду)
  if (templateConfig.dateRange && (templateConfig.dateRange.from || templateConfig.dateRange.to)) {
    const { from, to } = templateConfig.dateRange;

    if (from || to) {
      console.log('Применяем дополнительную фильтрацию по дате:', { from, to });

      filteredData = filteredData.filter(item => {
        const itemDate = item.createdTime;
        if (!itemDate) return true; // Если дата не указана, включаем запись

        const itemTimestamp = new Date(itemDate).getTime();

        if (from) {
          const fromTimestamp = new Date(from + 'T00:00:00').getTime();
          if (itemTimestamp < fromTimestamp) return false;
        }

        if (to) {
          const toTimestamp = new Date(to + 'T23:59:59').getTime();
          if (itemTimestamp > toTimestamp) return false;
        }

        return true;
      });

      console.log(`После фильтрации по дате осталось записей: ${filteredData.length}`);
    }
  }

  // Применяем фильтры
  if (templateConfig.filters && templateConfig.filters.length > 0) {
    templateConfig.filters.forEach(filter => {
      const beforeCount = filteredData.length;
      filteredData = applyFilter(filteredData, filter);
      console.log(`Фильтр "${filter.field} ${filter.operator} ${filter.value}": ${beforeCount} → ${filteredData.length}`);
    });
  }

  // Применяем группировку
  let groupedData = {};
  if (templateConfig.grouping && templateConfig.grouping.length > 0) {
    groupedData = applyGrouping(filteredData, templateConfig.grouping);
  } else {
    // Если группировки нет, создаем одну группу со всеми данными
    groupedData = { 'Все записи': filteredData };
  }

  // Применяем агрегацию к каждой группе
  const processedData = Object.entries(groupedData).map(([groupKey, groupItems]) => {
    const aggregatedData = {};

    if (templateConfig.aggregations && templateConfig.aggregations.length > 0) {
      console.log('Применяем агрегации к группе:', {
        groupKey,
        itemsCount: groupItems.length,
        aggregations: templateConfig.aggregations
      });

      templateConfig.aggregations.forEach(aggregation => {
        aggregatedData[aggregation.outputField] = calculateAggregation(groupItems, aggregation);
      });
    }

    return {
      groupKey,
      count: groupItems.length,
      items: groupItems,
      ...aggregatedData
    };
  });

  // Создаем общую сводку
  const summary = {
    totalRecords: filteredData.length,
    totalGroups: processedData.length,
    processedAt: new Date().toISOString()
  };

  return { processedData, summary };
};

/**
 * Применяет фильтр к данным
 * @param {Array} data - данные для фильтрации
 * @param {Object} filter - конфигурация фильтра
 * @returns {Array} - отфильтрованные данные
 */
const applyFilter = (data, filter) => {
  return data.filter(item => {
    const fieldValue = getNestedValue(item, filter.field);

    switch (filter.operator) {
      case 'equals':
        return String(fieldValue) === String(filter.value);
      case 'not_equals':
        return String(fieldValue) !== String(filter.value);
      case 'contains':
        return String(fieldValue).toLowerCase().includes(String(filter.value).toLowerCase());
      case 'not_contains':
        return !String(fieldValue).toLowerCase().includes(String(filter.value).toLowerCase());
      case 'starts_with':
        return String(fieldValue).toLowerCase().startsWith(String(filter.value).toLowerCase());
      case 'ends_with':
        return String(fieldValue).toLowerCase().endsWith(String(filter.value).toLowerCase());
      case 'greater_than':
        return parseFloat(fieldValue) > parseFloat(filter.value);
      case 'less_than':
        return parseFloat(fieldValue) < parseFloat(filter.value);
      case 'greater_equal':
        return parseFloat(fieldValue) >= parseFloat(filter.value);
      case 'less_equal':
        return parseFloat(fieldValue) <= parseFloat(filter.value);
      case 'is_empty':
        return !fieldValue || fieldValue === '';
      case 'is_not_empty':
        return fieldValue && fieldValue !== '';
      default:
        return true;
    }
  });
};

/**
 * Применяет группировку к данным
 * @param {Array} data - данные для группировки
 * @param {Array} groupingConfig - конфигурация группировки
 * @returns {Object} - сгруппированные данные
 */
const applyGrouping = (data, groupingConfig) => {
  let result = { 'Все записи': data };

  groupingConfig.forEach(grouping => {
    const newResult = {};

    Object.entries(result).forEach(([parentKey, parentItems]) => {
      const grouped = groupItemsByField(parentItems, grouping);

      Object.entries(grouped).forEach(([groupKey, groupItems]) => {
        const newKey = parentKey === 'Все записи' ? groupKey : `${parentKey} → ${groupKey}`;
        newResult[newKey] = groupItems;
      });
    });

    result = newResult;
  });

  return result;
};

/**
 * Группирует элементы по полю
 * @param {Array} items - элементы для группировки
 * @param {Object} grouping - конфигурация группировки
 * @returns {Object} - сгруппированные элементы
 */
const groupItemsByField = (items, grouping) => {
  const groups = {};

  items.forEach(item => {
    let groupKey = '';
    const fieldValue = getNestedValue(item, grouping.field);

    if (grouping.type === 'substring' && typeof fieldValue === 'string') {
      const startIndex = grouping.startIndex || 0;
      const length = grouping.length || 4;
      groupKey = fieldValue.substring(startIndex, startIndex + length) || 'Пустое значение';
    } else if (grouping.type === 'range' && grouping.ranges) {
      const numValue = parseFloat(fieldValue);
      const range = grouping.ranges.find(r =>
        numValue >= parseFloat(r.min) && numValue <= parseFloat(r.max)
      );
      groupKey = range ? range.label : 'Вне диапазона';
    } else {
      groupKey = String(fieldValue || 'Пустое значение');
    }

    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(item);
  });

  return groups;
};

/**
 * Вычисляет агрегацию для группы элементов
 * @param {Array} items - элементы группы
 * @param {Object} aggregation - конфигурация агрегации
 * @returns {*} - результат агрегации
 */
const calculateAggregation = (items, aggregation) => {
  console.log('calculateAggregation вызвана:', {
    type: aggregation.type,
    field: aggregation.field,
    stageName: aggregation.stageName,
    itemsCount: items.length
  });

  if (items.length === 0) return 0;

  const values = items.map(item => getNestedValue(item, aggregation.field))
    .filter(value => value !== null && value !== undefined);

  switch (aggregation.type) {
    case 'count':
      return items.length;
    case 'sum':
      return values.reduce((sum, val) => sum + parseFloat(val || 0), 0);
    case 'avg':
      return values.length > 0 ?
        values.reduce((sum, val) => sum + parseFloat(val || 0), 0) / values.length : 0;
    case 'min':
      return values.length > 0 ? Math.min(...values.map(v => parseFloat(v || 0))) : 0;
    case 'max':
      return values.length > 0 ? Math.max(...values.map(v => parseFloat(v || 0))) : 0;
    case 'unique':
      return new Set(values).size;

    // Временные агрегации для таймлайна
    case 'timeline_avg_total':
      // Среднее общее время прохождения всех стадий
      return calculateTimelineAggregation(items, 'avg', 'total');
    case 'timeline_min_total':
      // Минимальное общее время
      return calculateTimelineAggregation(items, 'min', 'total');
    case 'timeline_max_total':
      // Максимальное общее время
      return calculateTimelineAggregation(items, 'max', 'total');
    case 'timeline_avg_stage':
      // Среднее время на конкретной стадии
      return calculateTimelineAggregation(items, 'avg', 'stage', aggregation.stageName);
    case 'timeline_min_stage':
      // Минимальное время на стадии
      return calculateTimelineAggregation(items, 'min', 'stage', aggregation.stageName);
    case 'timeline_max_stage':
      // Максимальное время на стадии
      return calculateTimelineAggregation(items, 'max', 'stage', aggregation.stageName);

    default:
      return 0;
  }
};

/**
 * Вычисляет временные агрегации для таймлайна
 * @param {Array} items - элементы группы
 * @param {string} operation - тип операции (avg, min, max, sum)
 * @param {string} scope - область расчета (total, stage)
 * @param {string} stageName - название стадии (для scope='stage')
 * @returns {number} - результат в часах
 */
const calculateTimelineAggregation = (items, operation, scope, stageName = null) => {
  const timelineFieldId = 'ufCrm3_1757404689894'; // Поле "Таймлайн цеха"

  console.log('calculateTimelineAggregation вызвана:', {
    itemsCount: items.length,
    operation,
    scope,
    stageName,
    firstItem: items[0],
    timelineField: items[0]?.[timelineFieldId]
  });

  const durations = [];

  items.forEach((item, index) => {
    const timelineText = getNestedValue(item, timelineFieldId);

    if (index === 0) {
      console.log('Первый элемент - поле таймлайна:', {
        fieldId: timelineFieldId,
        value: timelineText,
        allKeys: Object.keys(item).filter(k => k.includes('timeline') || k.includes('1757404689894'))
      });
    }

    if (!timelineText) return;

    if (scope === 'total') {
      // Общее время от первой до последней стадии
      const totalDuration = getTotalDuration(timelineText);
      if (totalDuration && totalDuration.durationHours) {
        durations.push(parseFloat(totalDuration.durationHours));
      }
    } else if (scope === 'stage' && stageName) {
      // Время на конкретной стадии
      const events = parseTimeline(timelineText);
      const stageDurations = calculateStageDurations(events);

      console.log(`Поиск стадии "${stageName}" среди:`, stageDurations.map(d => d.stage));

      const stageDuration = stageDurations.find(d =>
        d.stage.toLowerCase().includes(stageName.toLowerCase())
      );

      if (stageDuration && stageDuration.durationHours) {
        durations.push(stageDuration.durationHours);
      }
    }
  });

  console.log('Найдено длительностей:', durations.length, durations);

  if (durations.length === 0) return 0;

  let result;
  switch (operation) {
    case 'avg':
      result = (durations.reduce((sum, val) => sum + val, 0) / durations.length).toFixed(2);
      break;
    case 'min':
      result = Math.min(...durations).toFixed(2);
      break;
    case 'max':
      result = Math.max(...durations).toFixed(2);
      break;
    case 'sum':
      result = durations.reduce((sum, val) => sum + val, 0).toFixed(2);
      break;
    default:
      result = 0;
  }

  console.log('Результат агрегации:', result);
  return result;
};

/**
 * Получает вложенное значение из объекта по пути
 * @param {Object} obj - объект
 * @param {string} path - путь к значению
 * @returns {*} - значение
 */
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

/**
 * Генерирует заголовок и текст новости на основе результатов отчета
 * @param {Object} templateConfig - конфигурация шаблона
 * @param {Object} reportResult - результаты применения шаблона
 * @returns {Object} - объект с title и text для новости
 */
export const generateNewsFromReport = (templateConfig, reportResult) => {
  const { processedData, summary } = reportResult;

  // Генерируем заголовок
  const title = templateConfig.name || 'Отчет по данным';

  // Генерируем текст отчета
  let text = `📊 ${title}\n\n`;

  if (templateConfig.description) {
    text += `${templateConfig.description}\n\n`;
  }

  text += `📈 Общая статистика:\n`;
  text += `• Всего записей: ${summary.totalRecords}\n`;
  text += `• Групп: ${summary.totalGroups}\n`;
  text += `• Обработано: ${formatDate(summary.processedAt)}\n\n`;

  // Добавляем информацию по группам
  if (processedData.length > 0) {
    text += `📋 Детальная информация:\n\n`;

    processedData.forEach((group, index) => {
      text += `${index + 1}. ${group.groupKey}\n`;
      text += `   Записей: ${group.count}\n`;

      // Добавляем агрегированные данные
      Object.entries(group).forEach(([key, value]) => {
        if (!['groupKey', 'count', 'items'].includes(key)) {
          const aggregationConfig = templateConfig.aggregations?.find(a => a.outputField === key);
          const label = aggregationConfig?.label || key;

          if (typeof value === 'number') {
            text += `   ${label}: ${formatNumber(value)}\n`;
          }
        }
      });
      text += '\n';
    });
  }

  text += `\n⏰ Сгенерировано автоматически из шаблона отчета "${templateConfig.name}"`;

  return { title, text };
};

/**
 * Форматирует число для отображения
 * @param {number} num - число
 * @returns {string} - отформатированное число
 */
const formatNumber = (num) => {
  if (Number.isInteger(num)) {
    return num.toLocaleString('ru-RU');
  }
  return num.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/**
 * Форматирует дату для отображения
 * @param {string|Date} date - дата
 * @returns {string} - отформатированная дата
 */
const formatDate = (date) => {
  return new Date(date).toLocaleString('ru-RU');
};

/**
 * Получает список сохраненных шаблонов из localStorage
 * @returns {Array} - массив шаблонов
 */
export const getSavedTemplates = () => {
  try {
    const saved = localStorage.getItem('reportTemplates');
    return saved ? JSON.parse(saved) : [];
  } catch (error) {
    console.error('Ошибка загрузки шаблонов:', error);
    return [];
  }
};

/**
 * Получает шаблон по ID
 * @param {number} templateId - ID шаблона
 * @returns {Object|null} - шаблон или null
 */
export const getTemplateById = (templateId) => {
  const templates = getSavedTemplates();
  return templates.find(t => t.id === templateId) || null;
};